import React, { useMemo } from "react";
import { Alert, Button, Card, Empty, List, message, Modal, Space, Tag, Tooltip, Typography } from "antd";
import { formatDateTime } from "../../../../../utils/dateUtils";
import { useAdoptAgentTrack } from "../../hooks/useScheduleDraftTrackMutations";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import { buildAgentAdoptConfirmLines, buildAgentAdoptPayload, getAgentAdoptBlockedReason, getAgentAdoptTargetFlightCount } from "../../lib/agentAdoptGuard";
import { AGENT_TRACK_TYPE, getAgentAircraftReg, getAgentEvaluationSummary, getAgentStatusLabel, getAgentStatusTone, getAgentStrategyLabel, getAgentTrackStatus, isAgentTrack } from "../../lib/agentTrackUtils";
import { formatScheduleDraftError } from "../../lib/errorUtils";

const { Paragraph, Text } = Typography;

function buildAgentSessionItems(sessions = [], tracks = []) {
    const agentTracks = tracks.filter(isAgentTrack);
    const tracksById = new Map(agentTracks.map((track) => [String(track.id), track]));
    const sessionTrackIds = new Set();

    const sessionItems = sessions.map((session, index) => {
        const track = session.track_id ? tracksById.get(String(session.track_id)) : null;
        const agIdentifier = session.ag_request_id || session.ag_run_id;
        if (session.track_id) {
            sessionTrackIds.add(String(session.track_id));
        }
        return {
            key: `session:${agIdentifier || session.track_id || index}`,
            agIdentifier,
            trackId: session.track_id,
            aircraftReg: session.aircraft_reg || getAgentAircraftReg(track) || "--",
            strategyLabel: session.strategy_label || getAgentStrategyLabel(track),
            status: session.status || getAgentTrackStatus(track),
            createdAt: session.created_at || track?.created_at,
            track,
        };
    });

    const trackOnlyItems = agentTracks
        .filter((track) => !sessionTrackIds.has(String(track.id)))
        .map((track) => ({
            key: `track:${track.id}`,
            agIdentifier: track.agent_context?.ag_request_id || track.agent_context?.ag_run_id,
            trackId: track.id,
            aircraftReg: getAgentAircraftReg(track) || "--",
            strategyLabel: getAgentStrategyLabel(track),
            status: getAgentTrackStatus(track),
            createdAt: track.created_at,
            track,
        }));

    return [...sessionItems, ...trackOnlyItems].sort((left, right) => new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime());
}

const AgentSessionPanel = ({ sessions = [], tracks = [], isFetching = false, error = null, onOpenTrack }) => {
    const {
        draftId,
        data: { refetchDraft, refetchAgentSessions, currentEditingTrack, isAgentWorking, localEditState, localCompareMatchState },
        permissions: { hasUpdatePermission },
    } = useScheduleDraftEditorWorkspace();
    const items = useMemo(() => buildAgentSessionItems(sessions, tracks), [sessions, tracks]);
    const { mutateAsync: adoptAgentTrack, isLoading: adoptingAgentTrack, variables: adoptingVariables } = useAdoptAgentTrack(draftId, {
        onSuccess: async () => {
            message.success("Agent 方案已追加到目标编辑轨");
            await Promise.all([refetchDraft?.(), refetchAgentSessions?.()]);
        },
        onError: (mutationError) => {
            message.error(formatScheduleDraftError(mutationError, "采纳方案失败"));
        },
    });

    const handleAdoptAgentTrack = (agentTrack) => {
        const blockedReason = getAgentAdoptBlockedReason({
            draftId,
            agentTrack,
            targetTrack: currentEditingTrack,
            hasUpdatePermission,
            isAgentWorking,
            flightDirtyState: localEditState?.dirtyState,
            compareDirtyState: localCompareMatchState?.dirtyState,
        });

        if (blockedReason) {
            message.warning(blockedReason);
            return;
        }

        const targetFlightCount = getAgentAdoptTargetFlightCount(currentEditingTrack);
        const confirmLines = buildAgentAdoptConfirmLines({ targetTrack: currentEditingTrack, targetFlightCount });

        Modal.confirm({
            title: targetFlightCount > 0 ? "确认追加到已有编辑轨" : "确认追加采纳 Agent 方案",
            content: (
                <Space direction="vertical" size={4}>
                    {confirmLines.map((line) => <Text key={line}>{line}</Text>)}
                </Space>
            ),
            okText: "确认追加",
            cancelText: "取消",
            okButtonProps: { danger: targetFlightCount > 0 },
            onOk: () => adoptAgentTrack({ trackId: agentTrack.id, values: buildAgentAdoptPayload(currentEditingTrack) }),
        });
    };

    return (
        <Card title="Agent 方案" className="schedule-draft-side-card" size="small">
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {error ? <Alert type="error" showIcon message="加载 Agent 会话失败" description={error.message || "未知错误"} /> : null}
                {items.length ? (
                    <List
                        loading={isFetching}
                        dataSource={items}
                        renderItem={(item) => {
                            const evaluationSummary = item.track ? getAgentEvaluationSummary(item.track) : item.status;
                            const canAdopt = item.track?.track_type === AGENT_TRACK_TYPE && String(item.status || "").toUpperCase() === "COMPLETED";
                            const isAdopting = adoptingAgentTrack && String(adoptingVariables?.trackId) === String(item.track?.id);
                            const adoptBlockedReason = canAdopt ? getAgentAdoptBlockedReason({
                                draftId,
                                agentTrack: item.track,
                                targetTrack: currentEditingTrack,
                                hasUpdatePermission,
                                isAgentWorking,
                                flightDirtyState: localEditState?.dirtyState,
                                compareDirtyState: localCompareMatchState?.dirtyState,
                            }) : "";
                            const actions = [
                                <Button key="open" size="small" type="link" disabled={!item.trackId} onClick={() => onOpenTrack?.(item.trackId)}>
                                    查看方案轨
                                </Button>,
                            ];

                            if (canAdopt) {
                                actions.push(
                                    <Tooltip key="adopt" title={adoptBlockedReason || "追加克隆到目标编辑轨，现有航班不会被替换"}>
                                        <span>
                                            <Button size="small" type="primary" loading={isAdopting} disabled={Boolean(adoptBlockedReason)} onClick={() => handleAdoptAgentTrack(item.track)}>
                                                追加采纳
                                            </Button>
                                        </span>
                                    </Tooltip>,
                                );
                            }

                            return (
                                <List.Item actions={actions}>
                                    <div className="schedule-draft-agent-session-item">
                                        <Space wrap size={[6, 6]}>
                                            <Tag color="blue">{item.aircraftReg}</Tag>
                                            <Tag color="purple">{item.strategyLabel}</Tag>
                                            <Tag color={getAgentStatusTone(item.status)}>{getAgentStatusLabel(item.status)}</Tag>
                                            {item.track ? <Tag>{item.track.name || "Agent 方案"}</Tag> : null}
                                        </Space>
                                        <Paragraph className="schedule-draft-agent-session-summary">
                                            {item.track?.summary || (String(item.status || "").toUpperCase() === "FAILED" ? "Agent 执行失败，请查看服务端日志或重试。" : "Agent 会话已创建，等待方案轨回填。")}
                                        </Paragraph>
                                        <Text type="secondary">评价：{evaluationSummary || "暂无"}</Text>
                                        <Text type="secondary">创建时间：{formatDateTime(item.createdAt) || "--"}</Text>
                                        {item.agIdentifier ? <Text type="secondary">AG #{item.agIdentifier}</Text> : null}
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <Empty description="暂无 Agent 编排会话" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Space>
        </Card>
    );
};

export default AgentSessionPanel;
